// src/DepthCard.js
//
// The console's one physical surface. Cards don't just sit on the page — they
// tilt toward the pointer in real perspective, and a specular highlight
// tracks the cursor across the face, so the card reads as a lit object rather
// than a rectangle with a drop shadow.
//
// Deliberately restrained: the tilt maxes out at ~5°, because this is an
// operations tool and a card that swings dramatically under the cursor gets
// tiring by the tenth time you cross it. The effect should register as "this
// is physical", not "look at this effect".
//
// Not for table rows — per-row tilt would make a dense table seasick and
// destroy the scanning the table exists for.
import React, { useCallback, useRef, useState } from 'react';
import { Paper, Box } from '@mui/material';
import { brand, surface, depth, radius, motion } from './designTokens';

const MAX_TILT = 5;      // degrees
const LIFT = 4;          // px toward the viewer on hover

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function DepthCard({
  children,
  onClick,
  active = false,
  interactive = true,
  sx = {},
  padding = 2.5,
  ...rest
}) {
  const ref = useRef(null);
  const raf = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [glare, setGlare] = useState({ x: 50, y: 0, on: false });

  const handleMove = useCallback((e) => {
    if (!interactive || prefersReducedMotion()) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;   // 0..1
    const py = (e.clientY - r.top) / r.height;   // 0..1
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      // Invert Y so pushing the cursor up tips the top of the card away,
      // which is how a real panel behaves under a light source.
      setTilt({ x: (0.5 - py) * MAX_TILT * 2, y: (px - 0.5) * MAX_TILT * 2 });
      setGlare({ x: px * 100, y: py * 100, on: true });
    });
  }, [interactive]);

  const handleLeave = useCallback(() => {
    if (raf.current) cancelAnimationFrame(raf.current);
    setTilt({ x: 0, y: 0 });
    setGlare((g) => ({ ...g, on: false }));
  }, []);

  const tilted = tilt.x !== 0 || tilt.y !== 0;

  return (
    // The perspective lives on a wrapper so the card itself can be transformed
    // without the parent's own transforms flattening it.
    <Box sx={{ perspective: '900px', display: 'flex', flex: '1 1 auto', minWidth: 0 }}>
      <Paper
        ref={ref}
        elevation={0}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          onClick(e);
        } : undefined}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          p: padding,
          borderRadius: `${radius.lg}px`,
          bgcolor: surface.card,
          border: `1px solid ${active ? brand.gold : surface.line}`,
          boxShadow: tilted ? `${depth.liftHover}, ${depth.ring}` : depth.lift,
          transformStyle: 'preserve-3d',
          transform: tilted
            ? `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) translateZ(${LIFT}px)`
            : 'rotateX(0deg) rotateY(0deg) translateZ(0)',
          // Snap toward the cursor quickly, settle back slowly — a heavy
          // object returning to rest rather than a rubber band.
          transition: tilted
            ? `box-shadow ${motion.fast}, border-color ${motion.fast}`
            : `transform ${motion.slow}, box-shadow ${motion.base}, border-color ${motion.base}`,
          cursor: onClick ? 'pointer' : 'default',
          // Warm light pooling behind the content.
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: depth.spotlight,
            pointerEvents: 'none',
          },
          '@media (prefers-reduced-motion: reduce)': {
            transform: 'none !important',
            transition: 'none !important',
          },
          ...sx,
        }}
        {...rest}
      >
        {/* Specular highlight — follows the pointer across the face. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: glare.on ? 1 : 0,
            transition: `opacity ${motion.base}`,
            background: `radial-gradient(circle 180px at ${glare.x}% ${glare.y}%, rgba(197,154,69,0.13), transparent 70%)`,
            '@media (prefers-reduced-motion: reduce)': { display: 'none' },
          }}
        />
        <Box sx={{ position: 'relative' }}>{children}</Box>
      </Paper>
    </Box>
  );
}
